// Lightweight axios wrapper. Auth is now carried by an httpOnly cookie
// set by the backend (`access_token`). CSRF protection rides on a sibling
// `csrf_token` cookie (non-httpOnly) which we echo into the X-CSRF-Token
// header on every state-changing request.
//
// Bearer-header fallback: some hosting environments (Cloudflare ingress
// with `Access-Control-Allow-Origin: *`) silently strip the
// `Set-Cookie` because browsers refuse to accept credentialed cookies
// from a wildcard origin. To keep the app working everywhere, we ALSO
// capture the `access_token` returned in the login response body and
// echo it back on every request as `Authorization: Bearer …`. The
// backend accepts both transports (see auth._extract_token).
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
if (!BASE) {
  console.warn("REACT_APP_BACKEND_URL is not set");
}

// One-time cleanup of the legacy localStorage key from older sessions.
try { localStorage.removeItem("chainflow:jwt"); } catch (_) { /* noop */ }

// ─── Bearer-token store ────────────────────────────────────────────────
// sessionStorage (cleared on tab close) keeps the token tab-scoped so
// signing out in one tab doesn't leak into another. localStorage would
// survive across tabs but at the cost of being readable by any script —
// sessionStorage is the right trade-off for an SPA.
const BEARER_KEY = "cf:bearer";
let _bearerMem = null;

const _readBearer = () => {
  if (_bearerMem) return _bearerMem;
  try { _bearerMem = sessionStorage.getItem(BEARER_KEY) || null; } catch (_) { _bearerMem = null; }
  return _bearerMem;
};

export const setBearerToken = (token) => {
  _bearerMem = token || null;
  try {
    if (token) sessionStorage.setItem(BEARER_KEY, token);
    else sessionStorage.removeItem(BEARER_KEY);
  } catch (_) { /* noop */ }
};

export const clearBearerToken = () => setBearerToken(null);

export const api = axios.create({
  baseURL: BASE,
  withCredentials: true, // send/receive the httpOnly access_token cookie when it survives
});

// ─── Session-expired pub/sub ────────────────────────────────────────────────
// Every 401 (other than the auth surfaces themselves) emits an
// `auth:session-expired` event so the AuthProvider can clear stale
// React state and route the user back to /login with a calm message.
// This is the only mechanism that prevents the "UI says logged in
// but every POST 401s" split-brain — never trust local state when
// the server says we are anonymous.
const SESSION_EXPIRED_EVT = "auth:session-expired";

// Routes we treat as auth surfaces — their own 401s are expected
// (bad password, invalid reset token, etc.) and must NOT trigger a
// global logout / redirect.
const AUTH_SURFACE_PATHS = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
  "/api/auth/verify-email",
  "/api/auth/resend-verification",
  "/api/auth/magic-link",
  "/api/auth/me",            // hydrate probe — handled directly by AuthProvider
  "/api/auth/realtime-token",
];

const _isAuthSurface = (url = "") => {
  for (const p of AUTH_SURFACE_PATHS) if (url.includes(p)) return true;
  return false;
};

// ─── CSRF: double-submit cookie ─────────────────────────────────────────────
// The backend mints a `csrf_token` cookie on login/register/reset/verify.
// We read it from `document.cookie` and echo it back in the X-CSRF-Token
// header on every state-changing request. GET/HEAD/OPTIONS are skipped.
const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "X-CSRF-Token";
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

const readCookie = (name) => {
  try {
    const cookies = document.cookie ? document.cookie.split("; ") : [];
    for (const c of cookies) {
      const idx = c.indexOf("=");
      if (idx > -1 && c.slice(0, idx) === name) return decodeURIComponent(c.slice(idx + 1));
    }
  } catch (_) { /* noop */ }
  return null;
};

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  // Attach Bearer token whenever we have one. Belt-and-braces with the
  // httpOnly cookie — whichever survives the ingress wins. We never
  // overwrite an explicitly-set Authorization header (e.g. magic-link
  // flows that supply their own token).
  const bearer = _readBearer();
  if (bearer) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization && !config.headers.authorization) {
      config.headers.Authorization = `Bearer ${bearer}`;
    }
  }
  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      config.headers = config.headers || {};
      config.headers[CSRF_HEADER] = csrf;
    }
  }
  return config;
});

// Single-flight guard so we never fire the session-expired event twice
// from a flurry of failing requests during page navigation.
let _expiredFired = false;
const _emitSessionExpired = () => {
  if (_expiredFired) return;
  _expiredFired = true;
  try {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVT));
  } catch (_e) { /* noop */ }
  // Reset the flag after a short window so a NEW login can re-arm.
  setTimeout(() => { _expiredFired = false; }, 4000);
};

// Soft-block: when the backend says email is unverified, route the user to
// /verify-email (not 401-logout). Skips auth routes so /verify-email and
// /resend-verification still work without infinite loops.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const status = err?.response?.status;
      const url    = err?.config?.url || "";
      const detail = err?.response?.data?.detail;
      const code   = (detail && typeof detail === "object") ? detail.code : null;

      // Calm 401 split-brain rescue. If a protected (non-auth-surface)
      // request 401s, the cookie is invalid → clear the React user
      // state and route back to /login with a "Session expired"
      // hint. Never let the UI lie about being signed in.
      if (status === 401 && !_isAuthSurface(url)) {
        _emitSessionExpired();
      }

      if (status === 403 && code === "email_unverified") {
        const path = window.location.pathname;
        if (!path.startsWith("/verify-email") && !path.startsWith("/login") && !path.startsWith("/signup")) {
          window.location.assign("/verify-email");
        }
      }
    } catch (_e) { /* noop */ }
    return Promise.reject(err);
  }
);

// Re-export for any consumers that want to listen explicitly.
export const AUTH_SESSION_EXPIRED_EVENT = SESSION_EXPIRED_EVT;

// ─── Operational error humanisation ─────────────────────────────────────────
// We never want a generic "Something went wrong" surfacing to the operator.
// Every failure should resolve to one of:
//   1. A precise server-emitted operational message (preferred)
//   2. A humanised Pydantic/FastAPI 422 with field labels
//   3. A network/timeout/HTTP-status sentence the operator can act on

const FIELD_LABELS = {
  first_name: "First name",
  last_name:  "Last name",
  phone:      "Mobile number",
  email:      "Email",
  role:       "Role",
  company:    "Company",
  pref:       "Preferred communication",
  status:     "Status",
  notes:      "Notes",
  tags:       "Tags",
  name:       "Name",
};

const _humanisePydantic = (e) => {
  if (!e || typeof e !== "object") return null;
  const loc = Array.isArray(e.loc) ? e.loc : [];
  // FastAPI prepends "body" / "query" / "path" — skip those.
  const field = [...loc].reverse().find((p) => p !== "body" && p !== "query" && p !== "path");
  const label = field && FIELD_LABELS[field] ? FIELD_LABELS[field] : (typeof field === "string" ? field : null);
  const raw = typeof e.msg === "string" ? e.msg : "Invalid value";
  // Strip the "Value error, " prefix Pydantic adds to custom validators.
  const msg = raw.replace(/^Value error,\s*/i, "");
  return label ? `${label}: ${msg}` : msg;
};

// FastAPI 422 returns {detail: [{msg, ...}, ...]}; we also use
// {detail: {code, message, field}} for structured app errors. Both supported.
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const lines = detail.map(_humanisePydantic).filter(Boolean);
    return lines.length ? lines.join(" · ") : null;
  }
  if (typeof detail === "object") {
    // Structured operational error: prefer human `message`, then Pydantic `msg`.
    if (typeof detail.message === "string" && detail.message) return detail.message;
    if (typeof detail.msg === "string" && detail.msg) return detail.msg;
  }
  return null;
};

// Map axios/HTTP failures → calm, operationally meaningful sentences.
// Never returns "Something went wrong"; every branch is actionable.
export const apiError = (err) => {
  const fromBody = formatApiErrorDetail(err?.response?.data?.detail);
  if (fromBody) return fromBody;
  // Axios-specific signal codes
  if (err?.code === "ERR_NETWORK")   return "Lost connection — check your internet and try again.";
  if (err?.code === "ECONNABORTED")  return "Request timed out — please try again.";
  if (err?.code === "ERR_CANCELED")  return "Request was cancelled.";
  const s = err?.response?.status;
  if (s === 401) return "Your session has expired — please sign in again.";
  if (s === 403) return "You don't have permission for this action.";
  if (s === 404) return "That record could not be found.";
  if (s === 409) return "That action conflicts with existing data.";
  if (s === 413) return "That request was too large to process.";
  if (s === 422) return "Some fields need attention.";
  if (s === 429) return "Too many attempts — please wait a moment and try again.";
  if (typeof s === "number" && s >= 500) return "ChainFlow is having trouble responding right now. Please try again in a moment.";
  // Last-resort string from axios itself, never the bland generic toast text.
  return err?.message || "Could not complete that action — please try again.";
};

// Convenience accessors for structured app-error responses (`{code, message, field}`).
export const apiErrorCode  = (err) => {
  const d = err?.response?.data?.detail;
  return (d && typeof d === "object" && !Array.isArray(d) && typeof d.code === "string") ? d.code : null;
};
export const apiErrorField = (err) => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d) && typeof d.field === "string") return d.field;
  // Fall back to first Pydantic loc field
  if (Array.isArray(d) && d[0] && Array.isArray(d[0].loc)) {
    const loc = [...d[0].loc].reverse().find((p) => p !== "body" && p !== "query" && p !== "path");
    return typeof loc === "string" ? loc : null;
  }
  return null;
};
